import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { posts as postsApi } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import UrgencyBadge from "../components/common/UrgencyBadge";
import StatusBadge from "../components/common/StatusBadge";
import LoadingOverlay from "../components/LoadingOverlay";
import { CATEGORY_LABELS } from "../lib/constants";

export default function ActivityPage() {
  const { user } = useAuth();
  const location = useLocation();
  const [tab, setTab] = useState("posts");
  const [myPosts, setMyPosts] = useState([]);
  const [myResponses, setMyResponses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      postsApi.getMyPosts(user.id),
      postsApi.getMyResponses(user.id),
    ]).then(([p, r]) => {
      setMyPosts(p.posts || []);
      setMyResponses(r.responses || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [user.id, location.key]);

  return (
    <div>
      <div className="flex gap-1 mb-6 bg-cream rounded-lg p-1 w-fit">
        {[
          { key: "posts", label: "My Posts" },
          { key: "responses", label: "My Responses" },
        ].map(({ key, label }) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              tab === key ? "bg-terracotta text-white" : "text-walnut-light hover:text-walnut"
            }`}
          >
            {label}
          </button>
        ))}
      </div>
      {loading && <LoadingOverlay />}
      {!loading && tab === "posts" && (
        <div className="space-y-3">
          {myPosts.length === 0 ? (
            <p className="text-walnut-light py-4">You haven't created any posts yet.</p>
          ) : (
            myPosts.map((post) => (
              <Link
                key={post.id}
                to={`/posts/${post.id}`}
                className="block p-4 bg-cream rounded-lg shadow-card hover:shadow-card-hover transition-shadow"
              >
                <div className="flex items-center gap-2 mb-1">
                  <UrgencyBadge urgency={post.urgency} />
                  <StatusBadge status={post.status} />
                  <span className="text-xs text-walnut-light">{CATEGORY_LABELS[post.category]}</span>
                  <span className="text-xs text-walnut-light/60 capitalize ml-auto">{post.type}</span>
                </div>
                <h3 className="font-display font-semibold text-walnut">{post.title}</h3>
                {post.takers_needed != null && (
                  <p className="text-xs text-walnut-light mt-1">{post.takers_filled} of {post.takers_needed} spots filled</p>
                )}
              </Link>
            ))
          )}
        </div>
      )}
      {!loading && tab === "responses" && (
        <div className="space-y-3">
          {myResponses.length === 0 ? (
            <p className="text-walnut-light py-4">You haven't responded to any posts yet.</p>
          ) : (
            myResponses.map((r) => (
              <Link
                key={r.id}
                to={`/posts/${r.post.id}`}
                className="block p-4 bg-cream rounded-lg shadow-card hover:shadow-card-hover transition-shadow"
              >
                <div className="flex items-center gap-2 mb-1">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                    r.status === "accepted" ? "text-sage-dark bg-sage/15" :
                    r.status === "rejected" ? "text-coral bg-coral/10" :
                    "text-walnut-light bg-stone/30"
                  }`}>
                    {r.status.charAt(0).toUpperCase() + r.status.slice(1)}
                  </span>
                  <span className="text-xs text-walnut-light">{CATEGORY_LABELS[r.post.category]}</span>
                  <span className="text-xs text-walnut-light/60 capitalize ml-auto">{r.post.type}</span>
                </div>
                <h3 className="font-display font-semibold text-walnut">{r.post.title}</h3>
                {r.completed_at && <p className="text-xs text-sage-dark mt-1">Completed</p>}
              </Link>
            ))
          )}
        </div>
      )}
    </div>
  );
}
